/* eslint-disable-next-line */
import React, { MutableRefObject, ReactNode, useEffect, useRef, useState } from 'react';
import { EnquiryContext } from '@vivli/features/enquiry/infastructure/context';
import { IDataComment, IDataInfoRequest, IEnquiry } from '@vivli/features/enquiry/infastructure/interface';
import { UseFieldArrayAppend, useFormContext, UseFormReturn } from 'react-hook-form';
import { Subject } from 'rxjs';
import { finalize, first } from 'rxjs/operators';
import {
    useActiveUserContext,
    useEnquiryApiContext,
    useModalApiContext,
    useMyDataRequestsApiContext,
    useToastApiContext
} from '@vivli/core/infrastructure/context';
import { IUser } from '@vivli/shared/infrastructure/interface';
import { TranslationComponent } from '@vivli/shared/components';
import { useEnquiryFormHook } from '@vivli/features/enquiry/infastructure/hook';
import { useStudyPrimaryRegistryUrlHook } from '@vivli/features/studies/infrastructure/hook';
import { useNavigate, useParams } from 'react-router';
import { EnquiryStatusEnum } from '@vivli/features/enquiry/infastructure/enum';
import { IDataRequest } from '@vivli/features/data-requests/infrastructure/interface';
import { useFormDirtyTracking } from '@vivli/shared/infrastructure/hook';

export interface EnquiryContextWrappersProps {
    children?: ReactNode;
    enquiry: IEnquiry;
    setEnquiry: React.Dispatch<React.SetStateAction<IEnquiry>>;
}

export const defaultDataInfoRequest: IDataInfoRequest = {
    id: 0,
    nctId: '',
    sponsorId: '',
    studyTitle: '',
    studyCompletionDate: null,
    orgId: '',
    isVivliPosted: false,
    isOnClinicalTrials: false,
    dataType: [],
    status: 1,
    notAvailableReason: 0,
    finalResponseDate: null,
    internalNotes: '',
    dataRequestsCreated: [],
    comments: [],
    registryUrl: '',
    createdBy: '',
    updatedBy: '',
    createdDate: new Date().toDateString(),
    updatedDate: '',
    environment: '',
    notify: false //this field is for marked notify and will not be persisted
};

export const EnquiryContextWrapper = ({ children, enquiry, setEnquiry }: EnquiryContextWrappersProps) => {
    const { user } = useActiveUserContext();

    let dirIndex: number;

    const [isLoading, setIsLoading] = useState(false);
    const [study, setStudy] = useState();
    let enquiryForm: IEnquiry;
    const isSavingRef = useRef(false);
    const dirAppendRef = useRef<UseFieldArrayAppend<IEnquiry>>();

    const enquiryService = useEnquiryApiContext();
    const modalService = useModalApiContext();
    const toastService = useToastApiContext();
    const formApi = useFormContext<IEnquiry>();
    const isFormDirty = useFormDirtyTracking(formApi);
    const { registryUrl } = useStudyPrimaryRegistryUrlHook();

    //load the enquiry into the form. reset() replaces defaultValues as well as values, so running it
    //over an edit in progress would make that edit the new baseline - and adding a comment calls
    //setEnquiry, which is exactly that case. So skip it while the form holds unsaved edits to the
    //record it is already showing. A *different* record still loads, dirty or not: getEnquiry has no
    //reset of its own, so skipping on identity alone would leave the form showing the previous
    //enquiry while state held the new one, and the next Save would post the stale values back.
    //After a save, resetAfterApiCall resets with keepDirty: false regardless of this effect.
    useEffect(() => {
        if (!enquiry) {
            return;
        }

        const isSameRecord = formApi.getValues('id') === enquiry.id;
        if (isFormDirty && isSameRecord) {
            return;
        }

        formApi.reset(enquiry, { keepErrors: true, keepIsValid: true });
        formApi.trigger();
    }, [enquiry]);
    const { addDataRequest } = useMyDataRequestsApiContext();
    const modalMaxWidth = '40em';
    const navigate = useNavigate();
    const { id } = useParams();
    const pageEndRef = useRef(null);
    const [openDirs, setOpenDirs] = useState<number[]>([]);
    const dirRefs = useRef<{ ref: MutableRefObject<HTMLDivElement> | null; dirIndex: number }[]>([]);

    const toastSuccessResponse = 'Item Successfully Added to My Requests';

    const modalDirStudyText = (
        <div>
            Enter a descriptive name for your research project.
            <p>
                If this is an additional <TranslationComponent>study</TranslationComponent> you want to add to the same project, then
                instead of entering a new project name here, click cancel and choose your previous project name from the drop-down on the
                "Request <TranslationComponent style={{ textTransform: 'capitalize' }}>study</TranslationComponent>" button.
            </p>
        </div>
    );
    const updateIsSaving = (status: boolean) => {
        isSavingRef.current = status;
        setIsLoading(status);
    };
    const handleSavingError = (err: string) => {
        updateIsSaving(false);
        modalService.error(`An error occurred with your operation. Please try again or contact Vivli support: ${err}`);
    };

    const handleFormSave = (isSubmit = false, notify = false) => {
        const subject = new Subject<IEnquiry>();

        formApi.handleSubmit(
            (formValues) => handleFormApiSave(formValues, isSubmit, subject, notify),
            () => {
                const formValues = formApi.getValues();
                handleFormApiSave(formValues, isSubmit, subject, notify);
            }
        )();

        return subject;
    };

    const handleFormApiSave = (formValues: IEnquiry, isSubmit: boolean, subject: Subject<IEnquiry>, notify = false) => {
        setIsLoading(true);
        enquiryService
            .updateEnquiry({ ...formValues } as IEnquiry, notify)
            .pipe(first())
            .subscribe((data) => {
                updateIsSaving(false);
                resetAfterApiCall(data);
                setIsLoading(false);
                toastService.success('Edits saved successfully');
            }, handleSavingError);
    };

    const updateStatus = (status: EnquiryStatusEnum) => {
        formApi.handleSubmit(
            (formValues) => updateStatusApi(status, formValues),
            () => {
                const formValues = formApi.getValues();
                updateStatusApi(status, formValues);
            }
        )();
    };
    const updateStatusApi = (status: EnquiryStatusEnum, formValues: IEnquiry) => {
        //an unsaved enquiry has no id, and the status endpoint takes identity from the body -
        //posting id 0 makes the api insert a brand new draft on every click. save first, then
        //transition the object the server hands back, which carries the real id.
        if (formValues.id === 0) {
            setIsLoading(true);
            enquiryService
                .updateEnquiry({ ...formValues } as IEnquiry, false)
                .pipe(first())
                .subscribe({
                    next: (savedEnquiry) => {
                        resetAfterApiCall(savedEnquiry);
                        doStatusChange(status, savedEnquiry);
                    },
                    error: handleSavingError
                });
            return;
        }

        doStatusChange(status, formValues);
    };

    const doStatusChange = (status: EnquiryStatusEnum, formValues: IEnquiry) => {
        setIsLoading(true);
        enquiryService
            .updateStatus({ ...formValues } as IEnquiry, status)
            .pipe(
                first(),
                finalize(() => setIsLoading(false))
            )
            .subscribe(
                (enq) => {
                    updateIsSaving(false);
                    resetAfterApiCall(enq);
                },
                (e) => {
                    let enq = { ...formValues } as IEnquiry;
                    resetAfterApiCall(enq);
                    handleError(e);
                }
            );
    };

    const resetAfterApiCall = (enq: IEnquiry) => {
        //preserve study links:
        //the form's dir array can be longer than the one in state (addNewDir only appends to the field array),
        //so index defensively - throwing here would skip the setEnquiry below and leave the saved id unknown
        enq.dataInfoRequests.forEach((dir, index) => {
            const existingDir = enquiry?.dataInfoRequests?.[index];
            if (existingDir?.studyLink) {
                enq.dataInfoRequests[index].studyLink = existingDir.studyLink;
            }
        });
        setEnquiry(enq);
        formApi.reset(enq, {
            keepErrors: true,
            keepIsValid: false,
            keepDirty: false
        });
    };
    const updateDirAfterApiCall = (dir: IDataInfoRequest, index: number) => {
        enquiryForm.dataInfoRequests[index] = dir;
        formApi.setValue('dataInfoRequests', enquiry.dataInfoRequests);
        setEnquiry(enquiryForm);
        setIsLoading(false);
    };
    const handleFailure = (input: string) => {
        modalService.message(
            `The Study for "${input}" was not found in the ClincialTrials.gov database.
        Please provide a descriptive "Study Title" for the study and any other information that will help the contributor to identify the desired study.`,
            { showLogo: true, showContinueButton: true, showBackground: false }
        );

        //this is needed to preserve newly added dataInfoRequest from disappearing
        resetAfterApiCall(enquiryForm);
    };

    const handleGetStudy = (nctIdorsponsorId: string, index: number) => {
        enquiryForm = formApi.getValues();
        dirIndex = index;
        enquiryService
            .getStudyInfo(nctIdorsponsorId)
            .pipe(first())
            .subscribe(
                (data) => {
                    const nctString = registryUrl(data);

                    const updatedDir: IDataInfoRequest = {
                        ...enquiry.dataInfoRequests[index],
                        nctId: data.nctId,
                        sponsorId: data.sponsorProtocolId,
                        studyTitle: data.studyTitle,
                        sponsor: data.leadSponsor.agency,
                        studyCompletionDate: data.actualStudyCompletionDate,
                        finalResponseDate: data.finalResponseDate,
                        registryUrl: nctString,
                        isVivliPosted: !!data.orgId,
                        studyLink: data.studyMetadataDoi
                    };
                    updateDirAfterApiCall(updatedDir, index);
                    setStudy(data);
                },
                () => handleFailure(nctIdorsponsorId)
            );
    };

    const initialGetStudy = (nctIdorsponsorId: string, index: number) => {
        enquiryForm = formApi.getValues();
        dirIndex = index;
        enquiryService
            .getStudyInfo(nctIdorsponsorId)
            .pipe(first())
            .subscribe(
                (data: any) => {
                    if (data) {
                        enquiry.dataInfoRequests[index].studyLink = data.studyMetadataDoi;
                    }
                    setEnquiry(enquiry);
                    setStudy(data);
                },
                () => handleFailure(nctIdorsponsorId)
            );
    };

    const getStudyLink = (index: number): string => {
        return enquiry.dataInfoRequests[index].studyLink ?? '';
    };
    const getStudyLinkBySponsor = (sponsor: string): string => {
        if (!sponsor) {
            return '';
        }
        let returnDir = enquiry.dataInfoRequests.find((dir) => dir.sponsorId === sponsor);
        if (!returnDir) {
            return '';
        }
        return returnDir.studyLink ?? '';
    };
    const handleError = (e) => {
        modalService.error(e);
    };
    const getEnquiry = (id: string) => {
        if (id) {
            setIsLoading(true);
            enquiryService
                .getEnquiry(id)
                .pipe(
                    first(),
                    finalize(() => setIsLoading(false))
                )
                .subscribe((enq) => {
                    setEnquiry(enq);
                }, handleError);
        }
    };

    const addComment = (dir: IDataInfoRequest, dirIndex: number, comment: string) => {
        const commentData: IDataComment = {
            id: 0,
            userId: user.userId,
            displayName: user.displayName,
            enquiryId: 0,
            dataInformationRequestId: 0,
            comment: comment,
            createdBy: user.userId,
            updatedBy: '',
            createdDate: new Date().toISOString(),
            updatedDate: null,
            environment: ''
        };

        //write the comment through the Form API rather than pushing onto the array in place. The DIR
        //the grid hands back is a live reference into the form's values, so a push does land in what
        //gets saved - but react-hook-form is never told, so nothing re-renders and the form never
        //reads as dirty. Pushing as well as setting would append the comment twice.
        const comments = formApi.getValues(`dataInfoRequests.${dirIndex}.comments`) ?? [];
        formApi.setValue(`dataInfoRequests.${dirIndex}.comments`, [...comments, commentData], { shouldDirty: true });
        setNotify(dir, dirIndex);
    };

    const setNotify = (dir: IDataInfoRequest, dirIndex: number) => {
        dir.notify = true;
        enquiry.dataInfoRequests[dirIndex] = dir;
        setEnquiry({ ...enquiry });
        //notify is transient - [NotMapped] on the entity, and cleared once the notification sends - so
        //it can't carry the dirty state on its own. The comments write above is what does that.
        formApi.setValue(`dataInfoRequests.${dirIndex}.notify`, true, { shouldDirty: true });
    };

    const formatStatus = (status: EnquiryStatusEnum): string => {
        switch (status) {
            case EnquiryStatusEnum.Draft:
                return 'Draft';
            case EnquiryStatusEnum.EnquiryValidation:
                return 'Enquiry Validation';
            case EnquiryStatusEnum.Review:
                return 'Review';
            case EnquiryStatusEnum.Archived:
                return 'Archived';
            case EnquiryStatusEnum.Withdrawn:
                return 'Withdrawn';
        }
    };

    const getAddDirStudy = (enquiryId, dirId, receivedTitle, isNewRequest) => {
        setIsLoading(true);

        if (!isNewRequest) {
            handleAddStudy(enquiryId, dirId, receivedTitle);
            return;
        }
        // Modal for New Dir Study
        modalService.prompt(modalDirStudyText, '', {
            title: receivedTitle,
            promptLabel: 'Research Project Name',
            style: { maxWidth: modalMaxWidth, textAlign: 'left' },
            messageStyle: { textAlign: 'left' },
            confirmText: 'OK',
            cancelText: 'Cancel',
            onConfirm: ({ comment }) => {
                handleAddStudy(enquiryId, dirId, comment);
            },
            onCancel: () => setIsLoading(false)
        });
    };
    const handleAddStudy = (enquiryId: number, dirId: number, receivedTitle: string) => {
        enquiryService
            .addDirStudy(enquiryId, dirId, receivedTitle)
            .pipe(first())
            .subscribe(
                (data) => {
                    handleStudyAdded(data.item2);
                    setIsLoading(false);
                },
                (error) => {
                    modalService.error(error);
                    setIsLoading(false);
                }
            );
    };
    const handleStudyAdded = (dataRequest: IDataRequest) => {
        addDataRequest(dataRequest);
        toastService.success(toastSuccessResponse);
    };

    const displaySuccessToastAndScrollToBottom = (message: string, dirIndex: number) => {
        toastService.success(message);
        setTimeout(() => {
            const dirRef = dirRefs.current.find((ref) => ref.dirIndex === dirIndex);
            dirRef.ref?.current.scrollIntoView({ behavior: 'smooth' });
        }, 500);
    };

    const isDirOpen = (dirIndex: number) => {
        return openDirs.some((od) => od === dirIndex);
    };

    const setDirAsOpen = (dirIndex: number) => {
        setOpenDirs([...openDirs, dirIndex]);
    };

    const setDirAsClosed = (dirIndex: number) => {
        const filteredDirs = [...openDirs.filter((od) => od !== dirIndex)];
        setOpenDirs(filteredDirs);
    };

    const toggleDir = (dirIndex: number) => {
        if (isDirOpen(dirIndex)) {
            setDirAsClosed(dirIndex);
        } else {
            setDirAsOpen(dirIndex);
        }
    };

    const closeAllDirs = () => {
        setOpenDirs([]);
    };

    const addDirRef = (dirRef: MutableRefObject<HTMLDivElement>, dirIndex: number) => {
        dirRefs.current.push({ ref: dirRef, dirIndex });
    };

    const removeDirRef = (dirIndex: number) => {
        dirRefs.current = dirRefs.current.filter((od) => od.dirIndex !== dirIndex);
    };

    const handleDirArrayInit = (onAppend: UseFieldArrayAppend<IEnquiry>) => {
        dirAppendRef.current = onAppend;
    };

    const addNewDir = (dir: IDataInfoRequest) => {
        dirAppendRef.current(dir);
    };

    const provider = {
        isFormDirty,
        enquiry,
        setEnquiry,
        handleFormSave,
        handleGetStudy,
        isLoading,
        getEnquiry,
        handleError,
        updateStatus,
        addComment,
        formatStatus,
        setNotify,
        getAddDirStudy,
        displaySuccessToastAndScrollToBottom,
        pageEndRef,
        setDirAsOpen,
        setDirAsClosed,
        toggleDir,
        openDirs,
        isDirOpen,
        closeAllDirs,
        addDirRef,
        removeDirRef,
        handleDirArrayInit,
        addNewDir,
        study,
        initialGetStudy,
        getStudyLink, //this is left just in case we need to come back to using it
        getStudyLinkBySponsor
    };

    useEffect(() => {
        if (enquiry?.dataInfoRequests?.length < 2) {
            setDirAsOpen(0);
        }
    }, [enquiry]);

    //once state holds a server-assigned id, put it in the url so a refresh reloads this enquiry
    //instead of a blank draft. declarative on purpose - any path that sets the id fixes the url.
    useEffect(() => {
        if (enquiry?.id > 0 && !id) {
            navigate(`/admin/enquiry/${enquiry.id}`, { replace: true });
        }
    }, [enquiry?.id]);

    return <EnquiryContext.Provider value={provider}>{children}</EnquiryContext.Provider>;
};
